// @ts-nocheck
import { useState } from "react";
import { signupJambushrusti } from "@/services/jambushrustiAuth";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@libraryadmin/components/ui/button";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import {
  Library,
  Lock,
  Mail,
  Sparkles,
  ArrowRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  isLibraryAdminAuthenticated,
  loginLibraryAdmin,
} from "@libraryadmin/services/auth";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    if (!email.trim() || !password.trim()) {
      toast({
        title: "Missing login details",
        description: "Please enter your email or username and password.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);

    try {
      if (isSignup) {
        await signupJambushrusti(email, password, "library");
        toast({
          title: "Account created",
          description: "Your Library account is ready. Please sign in.",
        });
        setIsSignup(false);
        setPassword("");
        setIsLoading(false);
        return;
      }
      await loginLibraryAdmin(email.trim(), password);
      setIsLoading(false);
      toast({
        title: "Welcome Back",
        description: "Logged in successfully",
      });
      const destination = location.state?.from || "/library-admin";
      navigate(destination, { replace: true });
    } catch (error) {
      setIsLoading(false);
      toast({
        title: isSignup ? "Unable to create account" : "Unable to sign in",
        description:
          error instanceof Error ? error.message : "Authentication failed.",
        variant: "destructive",
      });
    }
  };

  if (isLibraryAdminAuthenticated()) {
    return <Navigate to="/library-admin" replace />;
  }

  return (
    <div className="library-admin-module relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-background p-4">
      {/* Background Ornaments */}
      <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="animate-fade-in relative z-10 w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-tr from-primary to-accent shadow-lg ring-4 ring-primary/10">
            <Library className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Jambushrushti Library
          </h1>
          <p className="mt-1 flex items-center justify-center gap-1 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Jain Heritage &
            Manuscript Management
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleLogin} noValidate className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Email / Username
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                autoComplete="username"
                aria-label="Email or username"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input-field pl-9"
                placeholder="librarian@jambushrushti.org"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <button
                type="button"
                onClick={() => {
                  toast({
                    title: "Contact Superadmin",
                    description:
                      "Please contact your superadmin or IT administrator to reset your password.",
                  });
                }}
                className="border-0 bg-transparent text-xs text-primary hover:underline"
              >
                Forgot?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type={showPassword ? "text" : "password"}
                autoComplete={isSignup ? "new-password" : "current-password"}
                aria-label="Password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="input-field pl-9 pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(current => !current)}
                className="absolute right-3 top-1/2 -translate-y-1/2 border-0 bg-transparent p-0 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full rounded-xl py-5 font-semibold"
          >
            {isLoading ? (
              isSignup ? (
                "Creating account..."
              ) : (
                "Authenticating..."
              )
            ) : (
              <span className="flex items-center justify-center gap-2">
                {isSignup ? "Create Library Account" : "Sign In to Dashboard"}{" "}
                <ArrowRight className="h-4 w-4" />
              </span>
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="text-md mt-8 border-t border-border/50 pt-4 text-center text-muted-foreground">
          Return to main website?{" "}
          <a href="/" className="font-semibold text-primary hover:underline">
            Go Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
